import { PaddingSystemProps } from '@rover/kibble/core/styledSystemPropTypes';

/** Common props for BottomSheet across web and native */
export type BottomSheetCommonProps = {
  /** The body. On React Native it also holds the buttons, since `FooterComponent` is web only. */
  children: React.ReactNode;
  /** Shows the sheet. It slides up when this turns `true` and back down when it turns `false`. */
  isOpen: boolean;
  /** Hides the ✕. Defaults to `false`. */
  hideCloseIcon?: boolean;
  /**
   * Swaps the ✕ for a drag handle, and a drag down calls `onRequestClose`. On web a press on the
   * handle does too. Defaults to `false`.
   */
  closeOnDragDown?: boolean;
  /**
   * Lets a press on the scrim, Escape, a drag down or Android's Back close the sheet. Defaults to
   * `true`. When `false`, web also hides the ✕.
   */
  isDismissible?: boolean;
  /**
   * Padding for the body, and on web the header row and footer. Defaults to `{ paddingX: '4x' }`,
   * which a value replaces rather than extends.
   */
  contentPaddingStyles?: Partial<PaddingSystemProps>;
  /**
   * Called when the reader asks to close the sheet. Set `isOpen` to `false` here, or the web sheet
   * stays open and the React Native one can't reopen.
   */
  onRequestClose: (payload?: unknown) => void;
  /** The header row, beside the ✕, such as the sheet's title as a `Heading`. */
  HeaderComponent?: React.ReactNode;
  /** Native-only height; omit to size to content and scroll. Array is [xxs, xs, sm, md, lg]. */
  height?: number | `${number}%` | number[];
  /**
   * Cap on the sheet's content. Web takes any CSS length and defaults to 80vh; native takes a
   * number, `n%` or `nvh` of the window and defaults to 90% of its container.
   */
  maxHeight?: number | string;
};

/** Default horizontal padding for bottom sheet content */
export const defaultContentPaddingStyles: Partial<PaddingSystemProps> = { paddingX: '4x' };

/** Props for dismiss button component */
export type DismissButtonProps = Pick<
  BottomSheetCommonProps,
  'isDismissible' | 'hideCloseIcon' | 'onRequestClose' | 'closeOnDragDown'
>;
